import React, { useState, useEffect, useCallback } from 'react';
import {
    Building2, Search, Plus, Pencil, X, Check, AlertTriangle,
    ChevronLeft, ChevronRight, RotateCcw
} from 'lucide-react';
import api from '../services/api';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import '../styles/SoggettiFatturazione.css';

/**
 * SoggettiFatturazione — le aziende a cui si intestano le fatture.
 *
 * Non sono pazienti: non hanno sesso, data di nascita, cartella
 * clinica o consensi. Tenerle in una schermata a parte evita di
 * riempire di colonne vuote l'anagrafica dei pazienti.
 *
 * I soggetti "persona" nascono dalla scheda del paziente, alla sua prima
 * fattura, come COPIA dell'anagrafica (migration 243): se poi la residenza
 * cambia, l'intestatario non cambia da solo. Qui si corregge il loro
 * indirizzo - indirizzo, CAP, citta', provincia, nazione - e niente altro:
 * nome, cognome e codice fiscale sono del paziente (3/10/2026, prova D3 di
 * Alex: la Cassa mandava qui a completare il CAP, e la riga della persona
 * non aveva «Modifica»).
 */

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

/* Quello che una persona manda al salvataggio: solo l'indirizzo. Il modulo
   ha in mano la riga intera, e mandarla tutta riscriverebbe anche nome e
   codice fiscale con quello che c'era - una riga in piu' nel registro per
   niente, e la porta aperta a cambiarli da qui. */
const CAMPI_PERSONA = ['indirizzo', 'cap', 'citta', 'provincia', 'nazione'];
const soloIndirizzo = (f) => Object.fromEntries(CAMPI_PERSONA.map((k) => [k, f[k] ?? '']));

const VUOTO = {
    tipo: 'azienda', denominazione: '', codice_fiscale: '', partita_iva: '',
    indirizzo: '', cap: '', citta: '', provincia: '', nazione: 'IT',
    codice_univoco: '', pec: '', note: '',
};

const SoggettiFatturazione = () => {
    const [soggetti, setSoggetti] = useState([]);
    const [totale, setTotale] = useState(0);
    const [pagine, setPagine] = useState(1);
    const [pagina, setPagina] = useState(1);
    const [caricamento, setCaricamento] = useState(true);

    const [ricerca, setRicerca] = useState('');
    const [tipo, setTipo] = useState('azienda');
    const [statoAttivo, setStatoAttivo] = useState('true');

    const [form, setForm] = useState(null);   // null = chiuso
    /* La scheda chiede conferma se si chiude con qualcosa di diverso da
       com'era: sfondo, X e «Annulla» passano tutti da chiudi(). L'istantanea
       si prende all'apertura (apri), il salvataggio chiude. */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco(form, versione);
    const chiedi = useUscitaProtetta(sporco);
    const apri = (f) => { setForm(f); setErrore(null); setVersione((v) => v + 1); };
    const chiudi = () => { if (chiedi()) setForm(null); };
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await api.get('/fatturazione/soggetti/elenco', {
                params: {
                    q: ricerca || undefined,
                    tipo: tipo || undefined,
                    attivo: statoAttivo || undefined,
                    pagina,
                },
            });
            const d = r.data.data;
            setSoggetti(d.soggetti || []);
            setTotale(d.totale);
            setPagine(d.pagine || 1);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare i soggetti'));
        } finally {
            setCaricamento(false);
        }
    }, [ricerca, tipo, statoAttivo, pagina]);

    useEffect(() => { carica(); }, [carica]);

    /* I paesi per la nazione del soggetto (migration 243): dal server,
       cosi' il modulo offre solo codici che il database accetta. */
    const [paesi, setPaesi] = useState([]);
    useEffect(() => {
        api.get('/fatturazione/paesi')
            .then((r) => setPaesi(r.data.data.paesi || []))
            .catch(() => setPaesi([]));
    }, []);

    /* La ricerca riparte dalla prima pagina: restare alla dodicesima
       con tre risultati confonde. */
    useEffect(() => { setPagina(1); }, [ricerca, tipo, statoAttivo]);

    const salva = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            if (form.id) {
                const corpo = form.tipo === 'persona' ? soloIndirizzo(form) : form;
                const r = await api.put(`/fatturazione/soggetti/${form.id}`, corpo);
                setAvviso(r.data.message);
            } else {
                const r = await api.post('/fatturazione/soggetti', form);
                setAvviso(r.data.message || 'Soggetto creato');
            }
            setForm(null);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    const cambiaStato = async (s) => {
        try {
            setErrore(null);
            const r = s.attivo
                ? await api.delete(`/fatturazione/soggetti/${s.id}`)
                : await api.post(`/fatturazione/soggetti/${s.id}/riattiva`);
            setAvviso(r.data.message);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel cambio di stato'));
        }
    };

    const nome = (s) => s.denominazione || `${s.cognome || ''} ${s.nome || ''}`.trim() || '—';
    /* La scheda di una persona: solo l'indirizzo (vedi CAMPI_PERSONA). */
    const persona = form?.tipo === 'persona';

    return (
        <div className="sog-wrap">
            <IntestazioneSezione
                titolo="Soggetti di fatturazione"
                icona={<Building2 size={22} aria-hidden="true" />}
                azione={
                    <button className="sog-btn sog-btn--principale"
                            onClick={() => apri({ ...VUOTO })}>
                        <Plus size={16} /> Nuova azienda
                    </button>
                }
            />

            {errore && !form && (
                <div className="sog-errore">
                    <AlertTriangle size={16} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={14} /></button>
                </div>
            )}
            {avviso && (
                <div className="sog-avviso">
                    <Check size={16} /> {avviso}
                    <button onClick={() => setAvviso(null)}><X size={14} /></button>
                </div>
            )}

            <div className="sog-filtri" data-non-trattiene>
                <div className="sog-ricerca">
                    <Search size={16} />
                    <input value={ricerca} onChange={(e) => setRicerca(e.target.value)}
                           placeholder={tipo === 'persona'
                               ? 'Cognome, nome, codice fiscale, città…'
                               : 'Ragione sociale, P.IVA, città…'} />
                </div>
                <div className="sog-scelte">
                    <button className={tipo === 'azienda' ? 'attivo' : ''}
                            onClick={() => setTipo('azienda')}>Aziende</button>
                    <button className={tipo === 'persona' ? 'attivo' : ''}
                            onClick={() => setTipo('persona')}>Persone</button>
                    <button className={tipo === '' ? 'attivo' : ''}
                            onClick={() => setTipo('')}>Tutti</button>
                </div>
                <div className="sog-scelte">
                    <button className={statoAttivo === 'true' ? 'attivo' : ''}
                            onClick={() => setStatoAttivo('true')}>Attivi</button>
                    <button className={statoAttivo === 'false' ? 'attivo' : ''}
                            onClick={() => setStatoAttivo('false')}>Disattivati</button>
                </div>
            </div>

            {caricamento ? (
                <div className="sog-stato">Caricamento…</div>
            ) : soggetti.length === 0 ? (
                <div className="sog-stato">Nessun soggetto corrisponde ai filtri.</div>
            ) : (
                <>
                    <div className="sog-conteggio">
                        {totale} soggett{totale === 1 ? 'o' : 'i'}
                        {pagine > 1 && ` — pagina ${pagina} di ${pagine}`}
                    </div>

                    <div className="rm-scorre">
                    <table className="sog-tabella">
                        <thead>
                            <tr>
                                <th>Denominazione</th>
                                <th>Partita IVA</th>
                                <th>Codice fiscale</th>
                                <th>Città</th>
                                <th>Fatt. elettronica</th>
                                <th className="num">Documenti</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {soggetti.map((s) => (
                                <tr key={s.id} className={s.attivo ? '' : 'sog-disattivo'}>
                                    <td>
                                        {nome(s)}
                                        {s.tipo === 'persona' && (
                                            <span className="sog-etichetta">
                                                persona{s.id_paziente ? ` · ${s.id_paziente}` : ''}
                                            </span>
                                        )}
                                    </td>
                                    <td className="mono">{s.partita_iva || '—'}</td>
                                    <td className="mono">{s.codice_fiscale || '—'}</td>
                                    <td>
                                        {s.citta || '—'}
                                        {s.provincia ? ` (${s.provincia})` : ''}
                                    </td>
                                    <td>
                                        {s.codice_univoco || s.pec ? (
                                            <span className="sog-si">
                                                {s.codice_univoco || 'via PEC'}
                                            </span>
                                        ) : (
                                            <span className="sog-manca">da completare</span>
                                        )}
                                    </td>
                                    <td className="num">{s.documenti}</td>
                                    <td className="num">
                                        <button className="sog-icona" title="Modifica"
                                                onClick={() => apri({ ...s })}>
                                            <Pencil size={15} />
                                        </button>
                                        <button className="sog-icona"
                                                title={s.attivo ? 'Disattiva' : 'Riattiva'}
                                                onClick={() => cambiaStato(s)}>
                                            {s.attivo ? <X size={15} /> : <RotateCcw size={15} />}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>

                    {pagine > 1 && (
                        <div className="sog-pagine">
                            <button disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
                                <ChevronLeft size={16} /> Precedente
                            </button>
                            <span>{pagina} / {pagine}</span>
                            <button disabled={pagina >= pagine} onClick={() => setPagina(pagina + 1)}>
                                Successiva <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* ---------- scheda ---------- */}
            {form && (
                <div className="sog-modale-sfondo" onClick={chiudi}>
                    <div className="sog-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="sog-modale-testata">
                            <span>{persona ? 'Modifica intestatario' : form.id ? 'Modifica soggetto' : 'Nuova azienda'}</span>
                            <button onClick={chiudi}><X size={18} /></button>
                        </div>
                        <div className="sog-modale-corpo">
                            {errore && (
                                <div className="sog-errore">
                                    <AlertTriangle size={16} /> {errore}
                                </div>
                            )}

                            {/* I DOCUMENTI EMESSI E NON STAMPATI SEGUONO LA
                                CORREZIONE (revisione del 3/10/2026). La stampa e
                                la fattura elettronica leggono l'intestatario
                                quando si generano, non all'emissione: chi corregge
                                lo sa prima di salvare. Se congelarli all'emissione
                                e' una domanda per Alex (registro delle richieste). */}
                            {form.id && Number(form.emessi_da_stampare) > 0 && (
                                <p className="sog-manca">
                                    {Number(form.emessi_da_stampare) === 1
                                        ? 'Un documento di questo intestatario è già emesso e non ancora stampato: alla stampa porterà i dati che salvi adesso, e così la sua fattura elettronica se si rigenera.'
                                        : `${form.emessi_da_stampare} documenti di questo intestatario sono già emessi e non ancora stampati: alla stampa porteranno i dati che salvi adesso, e così le loro fatture elettroniche se si rigenerano.`}
                                </p>
                            )}

                            {persona ? (
                                <>
                                    <p className="sog-nota">
                                        <b>{nome(form)}</b>
                                        {form.codice_fiscale ? <> · <span className="mono">{form.codice_fiscale}</span></> : ''}
                                        {form.id_paziente ? ` · paziente ${form.id_paziente}` : ''}
                                    </p>
                                    <p className="sog-nota">
                                        Nome, cognome e codice fiscale vengono dall&apos;anagrafica del
                                        paziente. Qui si correggono indirizzo, CAP, città, provincia e
                                        nazione dell&apos;intestatario delle fatture: la scheda del
                                        paziente non li cambia.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <label className="sog-campo">
                                        <span>Ragione sociale *</span>
                                        <input value={form.denominazione || ''}
                                               onChange={(e) => setForm({ ...form, denominazione: e.target.value })} />
                                    </label>

                                    <div className="sog-riga">
                                        <label className="sog-campo">
                                            <span>Partita IVA *</span>
                                            <input className="mono" maxLength={13} value={form.partita_iva || ''}
                                                   onChange={(e) => setForm({ ...form, partita_iva: e.target.value.replace(/\s/g, '') })} />
                                        </label>
                                        <label className="sog-campo">
                                            <span>Codice fiscale</span>
                                            <input className="mono" maxLength={16} value={form.codice_fiscale || ''}
                                                   onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} />
                                        </label>
                                    </div>
                                </>
                            )}

                            <label className="sog-campo">
                                <span>Indirizzo</span>
                                <input value={form.indirizzo || ''}
                                       onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} />
                            </label>

                            <div className="sog-riga">
                                <label className="sog-campo">
                                    <span>Città</span>
                                    <input value={form.citta || ''}
                                           onChange={(e) => setForm({ ...form, citta: e.target.value })} />
                                </label>
                                <label className="sog-campo sog-campo--stretto">
                                    <span>Provincia</span>
                                    <input maxLength={2} value={form.provincia || ''}
                                           onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })} />
                                </label>
                                <label className="sog-campo sog-campo--stretto">
                                    <span>CAP</span>
                                    <input maxLength={12} value={form.cap || ''}
                                           onChange={(e) => setForm({ ...form, cap: e.target.value })} />
                                </label>
                            </div>

                            <label className="sog-campo">
                                <span>Nazione *</span>
                                <select value={form.nazione || ''}
                                        onChange={(e) => setForm({ ...form, nazione: e.target.value })}>
                                    {!form.nazione && <option value="">— scegli —</option>}
                                    {paesi.map((p) => (
                                        <option key={p.codice} value={p.codice}>{p.nome} ({p.codice})</option>
                                    ))}
                                </select>
                            </label>

                            {!persona && (
                                <>
                                    <div className="sog-sezione">Fattura elettronica</div>
                                    <p className="sog-nota">
                                        Serve almeno uno dei due: senza, il documento non può essere
                                        recapitato allo SdI.
                                    </p>
                                    <div className="sog-riga">
                                        <label className="sog-campo">
                                            <span>Codice destinatario</span>
                                            <input className="mono" maxLength={7} value={form.codice_univoco || ''}
                                                   onChange={(e) => setForm({ ...form, codice_univoco: e.target.value.toUpperCase() })} />
                                        </label>
                                        <label className="sog-campo">
                                            <span>PEC</span>
                                            <input type="email" value={form.pec || ''}
                                                   onChange={(e) => setForm({ ...form, pec: e.target.value })} />
                                        </label>
                                    </div>

                                    <label className="sog-campo">
                                        <span>Note</span>
                                        <textarea rows={2} value={form.note || ''}
                                                  onChange={(e) => setForm({ ...form, note: e.target.value })} />
                                    </label>
                                </>
                            )}

                            <div className="sog-modale-azioni">
                                <button className="sog-btn" onClick={chiudi}>Annulla</button>
                                <button className="sog-btn sog-btn--principale" onClick={salva}
                                        disabled={inCorso || (persona
                                            ? !form.nazione
                                            : !form.denominazione || !form.partita_iva)}>
                                    {form.id ? 'Salva' : 'Crea'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SoggettiFatturazione;
